import { Helmet } from 'react-helmet-async';
import { useEffect, useMemo } from 'react';
import useClientIdentificationStatusQuery from 'hooks/webapi/client/useIdentificationStatusQuery.ts';
import * as R from 'Router/routes';
import { Card, Link } from 'components';
import PhoneIdentityIcon from 'assets/icons/phone-identity.svg?react';
import MagicLinkModal from 'pages/Registration/MagicLinkModal/MagicLinkModal';
import { useStore } from 'hooks/useStore';
import H1 from 'components/H/H1';
import { TwButton } from 'components/TwButton/TwButton';
import useOCRRedirect from './useOCRRedirect';
import useBreakpoint from 'hooks/useBreakpoint';
import { useTranslation } from 'react-i18next';

export default function ManualOCR() {
  const { t } = useTranslation();
  const [, setStoreManualIdentification] = useStore(
    (store) => store.manualIdentification,
  );
  const {
    redirectUserToSMSComponent,
    startMagicLinkFlow,
    hasAcceptedMagicLink,
    goToNextStep,
    closeMagicLinkModal,
  } = useOCRRedirect();
  const { data: identificationStatus } = useClientIdentificationStatusQuery();

  const { size } = useBreakpoint();

  const isMobile = size.index <= 2;

  const skipRoute = useMemo(() => {
    if (!isMobile) return R.MANUAL_UPLOAD_ID;
    if (
      identificationStatus?.bankAccountStatus &&
      ['APPROVED_WITH_INCOME', 'PSD2_PAYMENT_SENT_WITH_INCOME'].includes(
        identificationStatus?.bankAccountStatus,
      )
    ) {
      return R.PENDING;
    }
    return R.MANUAL_PSD2_BANKS;
  }, [identificationStatus?.bankAccountStatus, isMobile]);

  useEffect(() => {
    setStoreManualIdentification({ manualIdentification: true });
  }, [setStoreManualIdentification]);

  return (
    <>
      <Helmet>
        <title>{t('identification:html_title')}</title>
      </Helmet>

      <H1 dataTest="identification-title">{t('identification:title')}</H1>
      <p className="text-center lg:text-left" data-test="identification-info">
        {t('identification:info')}
      </p>

      <Card
        data-test="identification-card"
        className="flex-col rounded-none gap-5 border-0 md:mx-0 md:rounded-lg md:border my-5 px-5"
      >
        <p
          data-test="identification-phone_id_title"
          className="text-lg font-semibold"
        >
          {t('identification:phone_id_title')}
        </p>
        <div className="flex gap-5">
          <PhoneIdentityIcon
            className="flex-shrink-0 h-10 w-8"
            data-test="identification-phoneIdentityIcon"
          />
          <p className="font-light" data-test="identification-phone_id_info">
            {isMobile
              ? t('identification:mobile_ocr_id_info')
              : t('identification:phone_id_info')}
          </p>
        </div>
        <TwButton
          data-test="magic-link"
          onClick={isMobile ? redirectUserToSMSComponent : startMagicLinkFlow}
        >
          {isMobile
            ? t('identification:mobile_ocr_button')
            : t('identification:sms_button')}
        </TwButton>
        <p
          data-test="identification-phone_id_footer"
          className="text-muted text-sm font-light"
          dangerouslySetInnerHTML={{
            __html: t('identification:phone_id_footer'),
          }}
        />
      </Card>

      <Link className="ml-5 lg:ml-0" dataTest="skip" to={skipRoute}>
        {t('common:skip')}
      </Link>

      {hasAcceptedMagicLink && (
        <MagicLinkModal
          dataTest="modal"
          onSuccess={goToNextStep}
          onCancel={closeMagicLinkModal}
        />
      )}
    </>
  );
}
